'use client';

/**
 * DataTable: TanStack Table v8 with client-side sorting, pagination, row
 * selection, expandable rows and optionally controlled column visibility.
 *
 * Ported from glp-frontend `components/helpers/data-table/index.tsx`. The host
 * passes data and loading state; nothing here fetches. Differences from glp:
 * the loading indicator and empty state are props, `height` is an inline
 * style, page-size options are configurable, and glp's no-op
 * `onPageChange` / `onPageSizeChange` / `itemMaxCount` props are gone.
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type ExpandedState,
  type Row,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

import { cn } from '../../lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import './column-meta';

/**
 * The scroll area the glp list pages use: rows scroll inside a fixed-height
 * box under a sticky header from `lg` up, and the table flows with the page
 * below `lg`. Pass as `scrollAreaClassName` together with `stickyHeader`, and
 * give the card around the table `lg:flex-1 lg:min-h-0 flex flex-col`.
 */
export const DATA_TABLE_SCROLL_AREA_CLASS = 'overflow-x-auto lg:flex-1 lg:min-h-0 lg:overflow-auto';

/** Left unset, `pagination` turns itself on only past this many rows. */
export const PAGINATION_ROW_THRESHOLD = 10;

export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export interface DataTableProps<T> {
  data: T[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<T, any>[];
  /** Shows `loadingIndicator` in place of the empty state while there are no rows. */
  isLoading?: boolean;
  /** Defaults to a spinning lucide `Loader2`. */
  loadingIndicator?: React.ReactNode;
  /** Text shown in the empty-state cell when there are no rows. */
  emptyMessage?: string;
  /** Replaces the empty-state text entirely. */
  emptyState?: React.ReactNode;
  /** Called with the `id` field of every selected row. */
  onSelectionChange?: (selectedIds: string[]) => void;
  /** Fixed height for the table root, e.g. `'400px'` or `'calc(100vh - 22rem)'`. */
  height?: string | number;
  /** Force the pager on or off. Left unset, it shows past 10 rows. */
  pagination?: boolean;
  /** Initial page size. */
  pageSizeProp?: number;
  /** Choices in the page-size select. The current size is always included. */
  pageSizeOptions?: number[];
  headerClassName?: string;
  bodyClassName?: string;
  /** Classes for the table root. */
  className?: string;
  expanded?: ExpandedState;
  onExpandedChange?: (expanded: ExpandedState) => void;
  getRowCanExpand?: (row: Row<T>) => boolean;
  getSubRows?: (row: T) => T[] | undefined;
  /**
   * Content for an expanded row. Called with `row.original.subRows`, as in
   * glp-frontend.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  subRowContent?: (subRows: any) => React.ReactNode;
  stickyHeader?: boolean;
  /** Row click handler; rows get a pointer cursor only when provided. */
  onRowClick?: (row: T, event: React.MouseEvent<HTMLTableRowElement>) => void;
  /** Extra props for a row's `<tr>` (drag and drop handlers, classes); `className` is merged. */
  getRowProps?: (row: T) => React.HTMLAttributes<HTMLTableRowElement> & { draggable?: boolean };
  /** Change this (e.g. a filter signature) to send the table back to page 1. */
  resetPageOn?: unknown;
  /**
   * Classes for a scroll box around the table only, keeping the pager pinned
   * below it. Without this the table is not wrapped at all. See
   * `DATA_TABLE_SCROLL_AREA_CLASS`.
   */
  scrollAreaClassName?: string;
  /**
   * Controlled column visibility, in TanStack's shape. Pair it with
   * useColumnVisibility and the toolbar's column manager. Left out, the table
   * keeps its own internal state.
   */
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (next: VisibilityState) => void;
}

export function DataTable<T>(props: DataTableProps<T>) {
  const {
    data,
    columns,
    onSelectionChange,
    height,
    isLoading,
    loadingIndicator,
    pagination: paginationProp,
    expanded: controlledExpanded,
    onExpandedChange: controlledOnExpandedChange,
    getRowCanExpand,
    getSubRows,
    headerClassName,
    bodyClassName,
    className,
    subRowContent,
    pageSizeProp = 100,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    stickyHeader = false,
    emptyMessage = 'No records found.',
    emptyState,
    onRowClick,
    getRowProps,
    resetPageOn,
    scrollAreaClassName,
    columnVisibility: controlledVisibility,
    onColumnVisibilityChange: controlledOnVisibilityChange,
  } = props;

  const [rowSelection, setRowSelection] = useState({});
  const [internalVisibility, setInternalVisibility] = useState<VisibilityState>({});
  const columnVisibility =
    controlledVisibility !== undefined ? controlledVisibility : internalVisibility;
  // TanStack hands the change as a value or an updater; resolve it here so a
  // controlling page only ever sees the next state.
  const onColumnVisibilityChange = (
    updater: VisibilityState | ((prev: VisibilityState) => VisibilityState),
  ) => {
    const next = typeof updater === 'function' ? updater(columnVisibility) : updater;
    if (controlledOnVisibilityChange) {
      controlledOnVisibilityChange(next);
    } else {
      setInternalVisibility(next);
    }
  };
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [internalExpanded, setInternalExpanded] = useState<ExpandedState>({});

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeProp);

  const expanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;
  const onExpandedChange = (
    updater: ExpandedState | ((prev: ExpandedState) => ExpandedState),
  ) => {
    const next = typeof updater === 'function' ? updater(expanded) : updater;
    if (controlledOnExpandedChange) {
      controlledOnExpandedChange(next);
    } else {
      setInternalExpanded(next);
    }
  };

  // Callers can force the pager either way; left unset, short lists render
  // without one.
  const pagination = paginationProp ?? data.length > PAGINATION_ROW_THRESHOLD;

  // A new filter/search swaps the data out from under the pager, so the page
  // the user sat on no longer means anything. Callers signal that by changing
  // `resetPageOn`. Sorting is deliberately left alone. Absent the prop this
  // fires once on mount with `undefined`, a no-op since the page already is 1.
  useEffect(() => {
    setPage(1);
  }, [resetPageOn]);

  // Both derived rather than stored: a stored pageCount plus a clamping effect
  // raced the reset above, reinstating the old page from a stale closure.
  const pageCount = Math.max(1, Math.ceil(data.length / pageSize));
  // Deleting or filtering away the rows under the current page leaves `page`
  // pointing past the end, so the page actually rendered is clamped here.
  const currentPage = Math.min(page, pageCount);

  useEffect(() => {
    if (!onSelectionChange) return;
    const selectedIds: string[] = [];
    for (const [index, selected] of Object.entries(rowSelection)) {
      if (!selected) continue;
      const item = data[Number(index)] as { id?: unknown } | undefined;
      // The raw `id`, as glp-frontend reports it (numbers stay numbers).
      if (item) selectedIds.push(item.id as string);
    }
    onSelectionChange(selectedIds);
    // Same trigger as glp-frontend: only a selection change reports.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      expanded,
    },
    onExpandedChange,
    getRowCanExpand,
    getSubRows,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnVisibilityChange,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    // Paging is done below by slicing the sorted rows.
    manualPagination: true,
  });

  // Sorting runs over the FULL data set; the current page is sliced from the
  // sorted rows here (manualPagination keeps TanStack from paginating itself).
  const allRows = table.getRowModel().rows;
  const pageRows = pagination
    ? allRows.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : allRows;
  const visibleColumnCount = table.getVisibleLeafColumns().length || columns.length;

  const tableElement = (
    <Table wrapperClassName={stickyHeader ? 'relative w-full' : undefined}>
      <TableHeader className={stickyHeader ? 'sticky top-0 z-10 bg-white' : undefined}>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow
            key={headerGroup.id}
            className={cn(stickyHeader ? 'bg-white' : 'sticky top-0 bg-white', headerClassName)}
          >
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id} colSpan={header.colSpan} className='font-bold'>
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody className={bodyClassName}>
        {pageRows.length ? (
          pageRows.map((row) => {
            const rowProps = getRowProps?.(row.original);
            return (
              <React.Fragment key={row.id}>
                <TableRow
                  data-state={row.getIsSelected() ? 'selected' : undefined}
                  {...rowProps}
                  className={cn(onRowClick && 'cursor-pointer', rowProps?.className)}
                  onClick={onRowClick ? (event) => onRowClick(row.original, event) : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className='py-4'>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>

                {row.getIsExpanded() && (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className='bg-gray-50'>
                      {subRowContent?.((row.original as { subRows?: unknown } | undefined)?.subRows)}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })
        ) : (
          <TableRow>
            <TableCell colSpan={visibleColumnCount} className='h-24 text-center'>
              {isLoading ? (
                (loadingIndicator ?? (
                  <Loader2
                    aria-label='Loading'
                    className='mx-auto h-6 w-6 animate-spin text-global-green'
                  />
                ))
              ) : (
                (emptyState ?? <h2>{emptyMessage}</h2>)
              )}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  return (
    <div
      className={cn(
        // The system radius: clips the corners wherever this root scrolls.
        'flex w-full flex-col rounded-[19px]',
        !stickyHeader && 'overflow-y-auto',
        // Without this the root sizes to its content and overflows a
        // height-bounded parent, so the inner scroll area never engages.
        scrollAreaClassName && 'min-h-0 flex-1',
        className,
      )}
      style={height !== undefined ? { height } : undefined}
    >
      {scrollAreaClassName ? (
        <div className={cn('rounded-[19px]', scrollAreaClassName)}>{tableElement}</div>
      ) : (
        tableElement
      )}
      <div className='mt-4 flex items-center justify-center'>
        {pagination && (
          <DataTablePagination
            page={currentPage}
            pageSize={pageSize}
            pageCount={pageCount}
            pageSizeOptions={pageSizeOptions}
            setPage={setPage}
            setPageSize={setPageSize}
          />
        )}
      </div>
    </div>
  );
}

interface DataTablePaginationProps {
  page: number;
  pageSize: number;
  pageCount: number;
  pageSizeOptions: number[];
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
}

function DataTablePagination({
  page,
  pageSize,
  pageCount,
  pageSizeOptions,
  setPage,
  setPageSize,
}: DataTablePaginationProps) {
  // Stepping works off the clamped `page` the caller passes, not the raw state,
  // which may still point past the end after rows were filtered away.
  const goToFirstPage = () => setPage(1);
  const goToPreviousPage = () => setPage(Math.max(page - 1, 1));
  const goToNextPage = () => setPage(Math.min(page + 1, pageCount));
  const goToLastPage = () => setPage(pageCount);

  const options = pageSizeOptions.includes(pageSize)
    ? pageSizeOptions
    : [...pageSizeOptions, pageSize].sort((a, b) => a - b);

  // Every button is explicitly type='button': a bare <button> defaults to
  // submit, so paging a table rendered inside a <form> would submit that form.
  return (
    <div className='flex flex-wrap items-center justify-center gap-2 text-sm text-[#525252] sm:text-base'>
      <div className='flex items-center justify-center gap-2'>
        <button type='button' aria-label='First page' onClick={goToFirstPage}>
          <ChevronFirst className='h-6 w-6' />
        </button>
        <button type='button' aria-label='Previous page' onClick={goToPreviousPage}>
          <ChevronLeft className='h-5 w-5' />
        </button>

        <div className='flex items-center justify-center gap-2'>
          <p className='rounded-lg border border-slate-400 px-1.5'>{page}</p>
          <p> of {pageCount} pages</p>
        </div>
        <button type='button' aria-label='Next page' onClick={goToNextPage}>
          <ChevronRight className='h-5 w-5' />
        </button>
        <button type='button' aria-label='Last page' onClick={goToLastPage}>
          <ChevronLast className='h-6 w-6' />
        </button>
      </div>
      <div>
        <select
          aria-label='Rows per page'
          className='w-32 rounded-2xl border border-slate-400 bg-white px-2 py-0.5 sm:w-36'
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
          }}
        >
          {options.map((size) => (
            <option key={size} value={size}>
              {size} Per page
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export default DataTable;
